import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { sortBy } from '@frontend-site/shared/utils';
import * as _ from 'lodash';
import { PFieldAbstractComponent } from '../p-field-abstract';

@Component({
    selector: 'p-field-multi-select',
    template: `
        <p-field [isMandatory]="isMandatory" [label]="label" [labelTooltip]="labelTooltip" [isReadOnly]="isReadOnly || isDisabled">
            <span slot="readMode" [ngClass]="customClasses" *ngIf="!autoOpen">
                <ng-container *ngIf="!isNoneSelected; else noneSelected">
                    <p-chip-group
                        [attr.automation-id]="'p-field-multi-select_' + label"
                        [nameField]="nameField"
                        [list]="selectedValues"
                        [isReadonly]="true"
                    ></p-chip-group>
                </ng-container>

                <ng-template #noneSelected class="p-field-multi-select-item">
                    <span class="no-value-read-only" *ngIf="noValueText">
                        {{ noValueText }}
                    </span>
                    <span *ngIf="!noValueText">
                        {{ placeholder }}
                    </span>
                </ng-template>
            </span>

            <ng-container slot="editMode">
                <span *ngIf="autoOpen" [mat-menu-trigger-for]="optionsMenu"> </span>
                <span *ngIf="isNoneSelected && !autoOpen && noValueText" class="no-value-edit" [mat-menu-trigger-for]="optionsMenu">
                    {{ noValueText }}
                </span>
                <p-chip-group
                    *ngIf="!autoOpen && (!noValueText || !isNoneSelected)"
                    [attr.automation-id]="'p-field-multi-select_' + label"
                    [nameField]="nameField"
                    [list]="selectedValues"
                    [mat-menu-trigger-for]="optionsMenu"
                    (removeItem)="removeValue($event)"
                    [icon]="chipIcon"
                    [isSmallChip]="isSmallChip"
                    [isReadonly]="!!displayValueFormatter"
                ></p-chip-group>
            </ng-container>
            <mat-menu
                #optionsMenu="matMenu"
                (click)="$event.stopPropagation()"
                [class]="menuCustomClasses + ' no-padding embedded p-menu p-menu-multiple'"
            >
                <ng-template matMenuContent>
                    <div class="p-menu-container" fxLayout="column" (click)="$event.stopImmediatePropagation()">
                        <p-picker-multi-select
                            [style]="menuDynamicStyle"
                            [autoFocus]="true"
                            [list]="fullList"
                            [idField]="idField"
                            [nameField]="nameField"
                            [selectedValues]="pickerSelectedValues"
                            [enableSearch]="canSearch"
                            [enableClear]="clearAllEnabled"
                            [enableAddAll]="enableAddAll"
                            [addAll]="addAll"
                            (cancel)="optionsMenu.closed.emit()"
                            (apply)="applyDone($event); optionsMenu.closed.emit()"
                            [noData]="noData"
                            [groupField]="groupField"
                            [emptyOption]="emptyOption"
                            [disableForEmptyOption]="true"
                            [enableAddNew]="enableAddExternalItem"
                            [newItemText]="newItemText"
                            [newItemValidation]="externalItemValidation"
                            (addNew)="addExternalItemOption($event)"
                            [actions]="actions"
                            [selectedAction]="selectedAction"
                            (actionChanged)="actionChanged.emit($event)"
                        >
                        </p-picker-multi-select>
                    </div>
                </ng-template>
            </mat-menu>
        </p-field>
    `,
    styleUrls: ['./p-field-multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldMultiSelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldMultiSelect'
})
export class PFieldMultiSelectComponent extends PFieldAbstractComponent<(string | number)[]> implements AfterViewInit, OnChanges {
    @Input() label?: string;
    @Input() autoOpen: boolean = false;
    @Input() emptyOption?: any;
    @Input() sortValues?: boolean = false;
    @Input() shouldSortListByKey: boolean = true;
    @Input() public menuDynamicStyle: { [klass: string]: any } | null | undefined = undefined;
    @Input() menuCustomClasses?: string = '';
    @Input() displayValueFormatter?: (key: any) => string;
    @Input() noValueText?: string;

    /*
     * Enables the addition of external items to the selection list.
     * When set up, should provide the current items that are not part of the list (if exists)
     */
    @Input() enableAddExternalItem = false;
    @Input() externalItemValidation = /.*/;
    @Input() newItemText = 'Actions.addNewSelectOption';
    @Input() externalItemText?: string;
    @Input() externalItems?: string[];
    @Output() externalItemsChange: EventEmitter<string[]> = new EventEmitter();

    @ViewChild(MatMenuTrigger) matSelect: MatMenuTrigger = {} as MatMenuTrigger;

    private notSortedLis?: string[];
    private _listMap: any;

    private externalItemsOptions?: { id: string | number; name: string }[];

    @Input() set list(value: string[]) {
        this._listMap = _.keyBy<string>(value, this.idField);
        this.notSortedLis = value;
    }

    get list(): string[] {
        if ((this.groupField || !this.shouldSortListByKey) && this.notSortedLis) {
            return this.notSortedLis;
        }
        const listValues = _.values<string>(this._listMap);
        if (this.sortByNameField && listValues) {
            //@ts-ignore (type of `listValues` is not reflecting the actual type. Because the sortBy is
            //not recognize the `nameField` as a key of this array - so ignore)
            return listValues.sort(sortBy(this.nameField, 'ascending'));
        }
        return listValues;
    }

    get fullList(): (string | { id: string | number; name: string })[] {
        if (!this.enableAddExternalItem || !this.externalItemsOptions) {
            return this.list;
        }
        return [...this.list, ...this.externalItemsOptions];
    }

    @Input() idField = 'id';
    @Input() nameField = 'name';
    @Input() groupField = '';
    @Input() isMandatory = false;
    @Input() sortByNameField = false;
    @Input() sortByFieldName: string | undefined = undefined;
    @Input() clearAllEnabled = false;
    @Input() enableAddAll = false;
    @Input() addAll = false;
    @Input() isSmallChip = false;
    @Input() customClasses = '';
    @Input() showChips = false;
    @Input() isInEditMode = true;
    // May implement this or not according to need;
    @Input() placeholder = '';
    @Input() canSearch = false;
    @Input() noData = 'No Data Found';
    @Input() chipIcon?: (selectedValue: string) => string;
    @Input() actions?: any[];
    @Input() selectedAction?: any;
    @Output() actionChanged = new EventEmitter<any>();

    protected pickerSelectedValues: (string | number)[] = [];

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['externalItems'] && this.enableAddExternalItem) {
            this.externalItemsOptions = this.externalItems?.map((item) => {
                return this.mapExternalItemOption(item);
            });
            setTimeout(() => {
                this.setPickerSelectedValues();
            }, 500);
        }
    }

    searchPredicate =
        (substring: string) =>
        (item: any): boolean => {
            return _.toLower(item[this.nameField]).includes(_.trim(_.toLower(substring)));
        };

    removeValue(item: { id: number } | number | string): void {
        if (this.value === undefined) {
            return;
        }

        const valueToRemove: string | number = typeof item === 'object' ? item.id : item;
        const values = _.pull(this.value, valueToRemove);
        if (this.enableAddExternalItem && this.externalItems?.includes(`${valueToRemove}`)) {
            this.externalItems = this.externalItems.filter((externalItem) => externalItem !== `${valueToRemove}`);
            this.externalItemsChange.emit(this.externalItems);
        }

        this.changeValue(values);
        this.setPickerSelectedValues();
    }

    get selectedValues(): { id: number | string }[] {
        const options = [...(this.value || []), ...(this.externalItems || [])];
        return _.map(options, (id: string | number) => {
            if (this.emptyOption && id === this.emptyOption[this.idField]) {
                return this.emptyOption;
            }
            if (this.displayValueFormatter) {
                return this.displayValueFormatter(id);
            }
            return this._listMap[id] || this.findExternalItem(id) || id;
        });
    }

    touch(): void {
        this.markAsTouched();
    }

    changeValue(values: any): void {
        this.markAsTouched();

        if (!this.isReadOnly) {
            if (this.sortValues && values && values.length > 1) {
                this.value = [...values].sort((a: any, b: any) => a - b);
            } else {
                this.value = [...values];
            }
            this._onChange(this.value);
        }
    }

    ngAfterViewInit(): void {
        this.setPickerSelectedValues();
        if (this.autoOpen) {
            this.matSelect?.openMenu();
        }
    }

    clearAll(): void {
        this.changeValue([]);
    }

    get isNoneSelected(): boolean {
        return _.isEmpty(this.value);
    }

    applyDone(values: any[]): void {
        if (this.enableAddExternalItem) {
            values = this.handleExternalItems(values);
        }
        if (this.sortValues && values && values.length > 1) {
            this.value = values.sort((a: any, b: any) => a - b);
        } else {
            this.value = values;
        }
        this._onChange(this.value);
        this.setPickerSelectedValues();
    }

    private handleExternalItems<T>(values: T[]): T[] {
        const externalItems: string[] = [];
        values = values.filter((val) => {
            if (typeof val === 'string') {
                const inList = this._listMap[val];
                if (!inList) {
                    externalItems.push(val);
                }
                return !!inList;
            }
            return true;
        });
        this.setExternalItems(externalItems);
        return values;
    }

    protected addExternalItemOption(val: string | number): void {
        const externalItemsOptions = this.externalItemsOptions || [];

        if (externalItemsOptions.some((item) => item.id === val)) {
            return;
        }

        this.externalItemsOptions = [...externalItemsOptions, this.mapExternalItemOption(val)];
    }

    private findExternalItem(value: string | number): { id: number | string; name?: string } | undefined {
        const externalItem = this.externalItems?.find((item) => item === value);
        if (!externalItem) {
            return;
        }
        return { id: externalItem, name: this.getExternalItemName(externalItem) };
    }

    private setExternalItems(vals: string[]): void {
        this.externalItemsOptions = vals ? vals.map((val) => this.mapExternalItemOption(val)) : undefined;
        this.externalItems = vals;
        this.externalItemsChange.emit(vals);
    }

    private mapExternalItemOption(item: string | number): { id: string | number; name: string } {
        const name = this.getExternalItemName(item);
        return { id: item, name };
    }

    private getExternalItemName(item: string | number): string {
        if (this.externalItemText) {
            return `${item}${this.externalItemText}`;
        }
        return `${item}`;
    }

    private setPickerSelectedValues(): void {
        const external = this.enableAddExternalItem && this.externalItems?.length ? this.externalItems : [];
        this.pickerSelectedValues = [...(this.value ?? []), ...external];
    }
}
